import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || `list-${Date.now()}`;
}

export const listMyPlaylists = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("playlists")
      .select("id, slug, title, description, is_public, cover_url, created_at, updated_at")
      .eq("user_id", context.userId)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const createPlaylist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => {
    const d = raw as { title: string; description?: string; is_public?: boolean };
    if (!d?.title?.trim()) throw new Error("title required");
    return d;
  })
  .handler(async ({ data, context }) => {
    const base = slugify(data.title);
    let slug = base;
    for (let i = 2; i < 50; i++) {
      const { data: exists } = await context.supabase
        .from("playlists").select("id").eq("user_id", context.userId).eq("slug", slug).maybeSingle();
      if (!exists) break;
      slug = `${base}-${i}`;
    }
    const { data: row, error } = await context.supabase
      .from("playlists")
      .insert({ user_id: context.userId, slug, title: data.title.trim(), description: data.description ?? null, is_public: !!data.is_public })
      .select("*").single();
    if (error) throw new Error(error.message);
    return row;
  });

export const updatePlaylist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => raw as { id: string; title?: string; description?: string; is_public?: boolean })
  .handler(async ({ data, context }) => {
    const patch: { title?: string; description?: string | null; is_public?: boolean } = {};
    if (data.title !== undefined) patch.title = data.title;
    if (data.description !== undefined) patch.description = data.description ?? null;
    if (data.is_public !== undefined) patch.is_public = data.is_public;
    const { error } = await context.supabase.from("playlists").update(patch).eq("id", data.id).eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deletePlaylist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => raw as { id: string })
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("playlists").delete().eq("id", data.id).eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const addToPlaylist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => raw as { playlist_id: string; activation_id: string })
  .handler(async ({ data, context }) => {
    // Confirm ownership
    const { data: pl } = await context.supabase.from("playlists").select("id").eq("id", data.playlist_id).eq("user_id", context.userId).maybeSingle();
    if (!pl) throw new Error("playlist not found");
    const { data: last } = await context.supabase.from("playlist_items").select("position").eq("playlist_id", data.playlist_id).order("position", { ascending: false }).limit(1).maybeSingle();
    const pos = (last?.position ?? -1) + 1;
    const { error } = await context.supabase.from("playlist_items").upsert(
      { playlist_id: data.playlist_id, activation_id: data.activation_id, position: pos },
      { onConflict: "playlist_id,activation_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const removeFromPlaylist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => raw as { playlist_id: string; activation_id: string })
  .handler(async ({ data, context }) => {
    const { data: pl } = await context.supabase.from("playlists").select("id").eq("id", data.playlist_id).eq("user_id", context.userId).maybeSingle();
    if (!pl) throw new Error("playlist not found");
    const { error } = await context.supabase.from("playlist_items").delete().eq("playlist_id", data.playlist_id).eq("activation_id", data.activation_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getPlaylist = createServerFn({ method: "GET" })
  .inputValidator((raw: unknown) => raw as { id: string })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: pl, error } = await supabaseAdmin
      .from("playlists")
      .select("id, slug, title, description, is_public, cover_url, created_at, updated_at")
      .eq("id", data.id).maybeSingle();
    if (error) throw new Error(error.message);
    // Public endpoint: only serve playlists explicitly marked public. Owners
    // access their private playlists through authenticated surfaces
    // (listMyPlaylists + the management UI), not this shareable-by-ID route.
    if (!pl || !pl.is_public) throw new Error("not found");
    const { data: items } = await supabaseAdmin
      .from("playlist_items")
      .select("position, activation:activations(id, slug, title, subtitle, hero_image_url, series, slot, activation_number, publish_at, status)")
      .eq("playlist_id", data.id)
      .order("position", { ascending: true });
    const filtered = (items ?? []).filter((r) => {
      const a = r.activation as { status?: string } | null;
      return a?.status === "published";
    });
    return { playlist: pl, items: filtered };
  });
